// ─── NotifPlantillaEditor — modal editor plantillas notif por etapa (0658a) ──
// Editor de plantillas de notificación del workflow cross-artefacto (ADR-027.1).
// Tabs por canal (IN_APP / EMAIL / TEAMS / SLACK) con 6 textareas (asunto + cuerpo
// × 3 idiomas ES/EN/ZH). Indicador "✓" en cada tab si plantilla guardada activa.

import { useEffect, useState } from 'react';
import { C } from '../../../../lib/design';
import { useI18n } from '../../../../i18n';
import {
  fetchNotifPlantillas,
  upsertNotifPlantilla,
  CANALES_NOTIF,
  type NotifPlantillaItem,
  type CanalNotifWorkflow,
  type NotifPlantillaPayload,
} from '../../../platform/workflow/workflow-config.service';

interface NotifPlantillaEditorProps {
  etapaId: string;
  onClose: () => void;
}

const VARIABLES_DOCUMENTADAS = [
  '{{artefactoTipo}}',
  '{{artefactoTitulo}}',
  '{{artefactoCodigo}}',
  '{{artefactoUrl}}',
  '{{publicador.nombre}}',
  '{{publicador.email}}',
  '{{fechaPublicacion}}',
  '{{tenantNombre}}',
  '{{etapaTitulo}}',
];

function emptyPayload(): NotifPlantillaPayload {
  return {
    asuntoEs: '',
    asuntoEn: '',
    asuntoZh: '',
    cuerpoEs: '',
    cuerpoEn: '',
    cuerpoZh: '',
    activa: true,
  };
}

export function NotifPlantillaEditor({ etapaId, onClose }: NotifPlantillaEditorProps) {
  const { t } = useI18n();
  const tt = t.admin.workflow.plantillas;

  const [canalActivo, setCanalActivo] = useState<CanalNotifWorkflow>('IN_APP');
  const [plantillas, setPlantillas] = useState<Map<CanalNotifWorkflow, NotifPlantillaItem>>(
    new Map(),
  );
  const [payload, setPayload] = useState<NotifPlantillaPayload>(emptyPayload());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function cargar() {
    setLoading(true);
    setError(null);
    try {
      const filas = await fetchNotifPlantillas(etapaId);
      const m = new Map<CanalNotifWorkflow, NotifPlantillaItem>();
      filas.forEach((f) => m.set(f.canal, f));
      setPlantillas(m);
      hidratarPayload(canalActivo, m);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  function hidratarPayload(
    canal: CanalNotifWorkflow,
    mapa: Map<CanalNotifWorkflow, NotifPlantillaItem>,
  ) {
    const fila = mapa.get(canal);
    if (fila) {
      setPayload({
        asuntoEs: fila.asuntoEs,
        asuntoEn: fila.asuntoEn,
        asuntoZh: fila.asuntoZh,
        cuerpoEs: fila.cuerpoEs,
        cuerpoEn: fila.cuerpoEn,
        cuerpoZh: fila.cuerpoZh,
        activa: fila.activa,
      });
    } else {
      setPayload(emptyPayload());
    }
  }

  useEffect(() => {
    void cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [etapaId]);

  function handleCambiarCanal(canal: CanalNotifWorkflow) {
    setCanalActivo(canal);
    hidratarPayload(canal, plantillas);
  }

  async function handleGuardar() {
    setSubmitting(true);
    setError(null);
    try {
      const fila = await upsertNotifPlantilla(etapaId, canalActivo, payload);
      setPlantillas((prev) => {
        const m = new Map(prev);
        m.set(canalActivo, fila);
        return m;
      });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'flex-end',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          width: 540,
          height: '100vh',
          padding: 24,
          overflowY: 'auto',
          boxShadow: '-4px 0 16px rgba(0,0,0,0.1)',
          fontFamily: 'inherit',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
          }}
        >
          <h3 style={{ margin: 0, color: C.azul1, fontSize: 16 }}>{tt.tituloEditor}</h3>
          <button
            onClick={onClose}
            aria-label={tt.cerrar}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: 18,
              color: C.gray700,
            }}
          >
            ×
          </button>
        </div>

        {/* Tabs canal */}
        <div
          style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid #E5E7EB' }}
        >
          {CANALES_NOTIF.map((c) => {
            const fila = plantillas.get(c);
            const tieneActiva = fila?.activa ?? false;
            const labelCanal =
              c === 'IN_APP'
                ? tt.tabIn_app
                : c === 'EMAIL'
                  ? tt.tabEmail
                  : c === 'TEAMS'
                    ? tt.tabTeams
                    : tt.tabSlack;
            return (
              <button
                key={c}
                onClick={() => handleCambiarCanal(c)}
                style={{
                  padding: '8px 14px',
                  border: 'none',
                  background: canalActivo === c ? C.azul1 : 'transparent',
                  color: canalActivo === c ? '#fff' : C.gray700,
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: 600,
                  fontFamily: 'inherit',
                  borderRadius: '6px 6px 0 0',
                }}
              >
                {labelCanal}
                {tieneActiva && <span style={{ marginLeft: 6 }}>✓</span>}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div style={{ color: C.gray700, fontSize: 13 }}>{tt.cargando}</div>
        ) : (
          <>
            {/* Variables disponibles */}
            <div
              style={{
                background: '#F9FAFB',
                padding: 10,
                borderRadius: 6,
                marginBottom: 12,
                fontSize: 11,
                color: C.gray700,
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: 4 }}>{tt.variablesDisponibles}:</div>
              <div style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {VARIABLES_DOCUMENTADAS.join(' · ')}
              </div>
            </div>

            {/* 6 textareas: asunto + cuerpo × 3 idiomas */}
            {(['Es', 'En', 'Zh'] as const).map((suf) => {
              const idiomaLabel =
                suf === 'Es' ? tt.idiomaEs : suf === 'En' ? tt.idiomaEn : tt.idiomaZh;
              const asuntoKey = `asunto${suf}` as keyof NotifPlantillaPayload;
              const cuerpoKey = `cuerpo${suf}` as keyof NotifPlantillaPayload;
              return (
                <div key={suf} style={{ marginBottom: 16 }}>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: C.azul1,
                      marginBottom: 6,
                      textTransform: 'uppercase',
                    }}
                  >
                    {idiomaLabel}
                  </div>
                  <input
                    type="text"
                    value={payload[asuntoKey] as string}
                    onChange={(e) => setPayload((p) => ({ ...p, [asuntoKey]: e.target.value }))}
                    placeholder={tt.placeholderAsunto}
                    maxLength={200}
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      borderRadius: 6,
                      border: '1px solid #D1D5DB',
                      fontSize: 12,
                      fontFamily: 'inherit',
                      marginBottom: 6,
                      boxSizing: 'border-box',
                    }}
                  />
                  <textarea
                    value={payload[cuerpoKey] as string}
                    onChange={(e) => setPayload((p) => ({ ...p, [cuerpoKey]: e.target.value }))}
                    placeholder={tt.placeholderCuerpo}
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      borderRadius: 6,
                      border: '1px solid #D1D5DB',
                      fontSize: 12,
                      fontFamily: 'inherit',
                      resize: 'vertical',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
              );
            })}

            {/* Toggle activa */}
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 12,
                color: C.gray700,
                marginBottom: 16,
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={payload.activa ?? true}
                onChange={(e) => setPayload((p) => ({ ...p, activa: e.target.checked }))}
              />
              {tt.botonActiva}
            </label>

            {error && (
              <div
                style={{
                  background: '#FEF2F2',
                  border: '1px solid #FECACA',
                  color: '#991B1B',
                  padding: 10,
                  borderRadius: 6,
                  fontSize: 12,
                  marginBottom: 12,
                }}
              >
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={handleGuardar}
                disabled={submitting}
                style={{
                  padding: '10px 18px',
                  borderRadius: 6,
                  border: 'none',
                  background: submitting ? '#9CA3AF' : C.azul1,
                  color: '#fff',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  fontSize: 12,
                  fontWeight: 600,
                  fontFamily: 'inherit',
                }}
              >
                {tt.botonGuardar}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
